import { useMutation, useQuery } from "@apollo/client";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import { Button, Paper, Stack, Tooltip, Typography } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { DataGrid } from "components/DataGrid";
import { gql } from "generated-graphql";
import { Role } from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useTenant } from "hooks/useTenant";
import GET_ROLES_QUERY from "queries/getRoles";
import { useMemo, useState } from "react";

import { RoleEditorDialog, RoleEditorSaveInput } from "./RoleEditorDialog";

const CREATE_ROLE = gql(`
  mutation CreateRole($tenantId: ID!, $name: String!, $permissions: [Permission!]!) {
    createRole(tenantId: $tenantId, name: $name, permissions: $permissions) {
      id
      name
      permissions
    }
  }
`);

const UPDATE_ROLE = gql(`
  mutation UpdateRole($id: ID!, $name: String!, $permissions: [Permission!]!) {
    updateRole(id: $id, name: $name, permissions: $permissions) {
      id
      name
      permissions
    }
  }
`);

const DELETE_ROLE = gql(`
  mutation DeleteRole($id: ID!) {
    deleteRole(id: $id)
  }
`);

export function Roles() {
  useBreadcrumb({ label: "Roles" });

  const alerts = useAlerts();
  // Roles are scoped to whichever org is selected globally (via "change org"
  // in the top nav). Avoids a screen-local picker that would duplicate the
  // global selector and let the two drift apart.
  const { tenant, tenantId } = useTenant();
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);

  const { data, loading } = useQuery(GET_ROLES_QUERY, {
    variables: { tenantId: tenantId ?? "" },
    skip: !tenantId,
    fetchPolicy: "cache-and-network",
  });

  const refetchRoles = {
    refetchQueries: [{ query: GET_ROLES_QUERY, variables: { tenantId: tenantId ?? "" } }],
  };

  const [createRole, { loading: creating }] = useMutation(CREATE_ROLE, refetchRoles);
  const [updateRole, { loading: updating }] = useMutation(UPDATE_ROLE, refetchRoles);
  const [deleteRole, { loading: deleting }] = useMutation(DELETE_ROLE, refetchRoles);

  const roles = useMemo(() => {
    return [...(data?.roles ?? [])].sort((a, b) => a.name.localeCompare(b.name));
  }, [data?.roles]);

  const handleCreate = () => {
    setEditingRole(null);
    setEditorOpen(true);
  };

  const handleEdit = (role: Role) => {
    setEditingRole(role);
    setEditorOpen(true);
  };

  const handleSave = async ({ name, permissions }: RoleEditorSaveInput) => {
    try {
      if (editingRole) {
        await updateRole({ variables: { id: editingRole.id, name, permissions } });
        alerts.success("Role updated");
      } else {
        await createRole({ variables: { tenantId: tenantId ?? "", name, permissions } });
        alerts.success("Role created");
      }
      setEditorOpen(false);
      setEditingRole(null);
    } catch (error) {
      alerts.error(error instanceof Error ? error.message : "Failed to save role");
    }
  };

  const handleDelete = async () => {
    if (!roleToDelete) return;
    try {
      await deleteRole({ variables: { id: roleToDelete.id } });
      alerts.success("Role deleted");
    } catch (error) {
      alerts.error(error instanceof Error ? error.message : "Failed to delete role");
    } finally {
      setRoleToDelete(null);
    }
  };

  if (!tenant) {
    return (
      <Stack sx={{ p: 3 }} spacing={1}>
        <Typography variant="h6">No organization selected</Typography>
        <Typography variant="body2" color="text.secondary">
          Use <strong>change org</strong> at the top right to pick an organization, then its roles
          will appear here.
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack sx={{ p: 3 }} spacing={3}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        <Typography variant="h6">Roles for {tenant.name}</Typography>
        <Button variant="contained" onClick={handleCreate}>
          Create role
        </Button>
      </Stack>

      {/* Snap grid height to the row count (with a sensible floor for the empty
          state and a cap so a tenant with many custom roles still scrolls).
          Column-header is ~56px, each row ~38px at default density. */}
      <Paper
        variant="outlined"
        sx={{
          height: Math.min(520, 64 + Math.max(roles.length, 2) * 38),
          overflow: "hidden",
          borderRadius: 1,
        }}
      >
        <DataGrid<Role>
          rows={roles}
          loading={loading}
          getRowId={(row) => row.id}
          noRowsMessage="This organization has no roles yet."
          sx={{ border: "none" }}
          columns={[
            {
              field: "name",
              headerName: "Role",
              flex: 1,
              minWidth: 220,
            },
            {
              field: "userCount",
              headerName: "Users",
              width: 100,
              type: "number",
              sortable: false,
            },
            {
              field: "permissions",
              headerName: "Permissions",
              width: 140,
              type: "number",
              sortable: false,
              valueGetter: (_value, row) => row.permissions?.length ?? 0,
            },
            {
              field: "actions",
              type: "actions",
              sortable: false,
              align: "right",
              width: 96,
              getActions: (params) => [
                <Tooltip title="Edit role" key="edit">
                  <GridActionsCellItem
                    onClick={() => handleEdit(params.row)}
                    label="Edit role"
                    icon={<Edit />}
                  />
                </Tooltip>,
                <Tooltip title="Delete role" key="delete">
                  <GridActionsCellItem
                    onClick={() => setRoleToDelete(params.row)}
                    label="Delete role"
                    icon={<Delete />}
                    disabled={deleting}
                  />
                </Tooltip>,
              ],
            },
          ]}
        />
      </Paper>

      {editorOpen && (
        <RoleEditorDialog
          role={editingRole}
          tenantName={tenant.name ?? ""}
          saving={creating || updating}
          onClose={() => {
            setEditorOpen(false);
            setEditingRole(null);
          }}
          onSave={handleSave}
        />
      )}

      <ConfirmDialog
        open={!!roleToDelete}
        onClose={() => setRoleToDelete(null)}
        header={`Delete role "${roleToDelete?.name}"?`}
        confirmText="Yes, delete it"
        cancelText="Cancel"
        onConfirm={handleDelete}
        info={
          <Typography variant="body2">
            This permanently removes the role from {tenant.name}. Roles that are still assigned to
            users can&apos;t be deleted until those users are reassigned.
          </Typography>
        }
        loading={deleting}
        disableConfirm={!roleToDelete}
      />
    </Stack>
  );
}
